import { type RefObject, useLayoutEffect, useRef, useState } from 'react';

export type FitLevel = 0 | 1 | 2 | 3 | 4;

export const MAX_FIT_LEVEL = 4;

/** Pixels the content may exceed the screen by before it counts as overflow (sub-pixel rounding). */
const OVERFLOW_TOLERANCE_PX = 1;

/** The certified previews never get shorter than this; below it the page packs one level tighter instead. */
export const PREVIEW_MIN_PX = 96;
/** The preview before the first measurement, and on pages that are not fitted. */
export const PREVIEW_DEFAULT_PX = 140;
/** The tallest preview, as a share of the card's width. */
const PREVIEW_MAX_RATIO = 0.8;
/** Room under the first screen above which a page without a certified grid loosens one level; more than any level saves. */
const STEP_DOWN_ROOM_PX = 120;
/** The "more below" line that closes the first screen. */
export const FOLD_CUE_HEIGHT_PX = 40;

/** Marks the certified grid: its bottom edge, plus the cue, must land on the screen's bottom edge. */
export const FOLD_GRID_ATTR = 'data-area-fold-grid';
/** Marks where the sections below the first screen start, for pages with no certified grid. */
export const FOLD_ATTR = 'data-area-fold';

export type AreaFit = {
  /** 0 is the full design, 4 the tightest (no sparklines, no subtitle). */
  level: FitLevel;
  /** Height of each certified preview. */
  previewHeight: number;
  /** Space above the cue when the previews reach their tallest and the screen still has room. */
  foldGap: number;
  /** The first screen still runs past the bottom edge at the tightest level, so a cue there would be off screen. */
  overflows: boolean;
};

export const UNFITTED: AreaFit = { foldGap: 0, level: 0, overflows: false, previewHeight: PREVIEW_DEFAULT_PX };

/** The level after one measurement: one step tighter while the content overflows, never past the last level. */
export function nextFitLevel(level: FitLevel, overflowPx: number): FitLevel {
  return overflowPx > OVERFLOW_TOLERANCE_PX && level < MAX_FIT_LEVEL ? ((level + 1) as FitLevel) : level;
}

export type FoldMeasure =
  /** Room left under the certified grid, less the cue; negative when the grid runs past the screen. */
  | { kind: 'grid'; roomPx: number; rows: number; cardWidthPx: number }
  /** How far the first sections below the fold start past the screen's bottom edge. */
  | { kind: 'fold'; overflowPx: number };

/**
 * One fitting step. With a certified grid, the previews take the room under it (spread over its rows), up to
 * their tallest; what is left goes above the cue. When even the shortest preview overflows, the page packs one
 * level tighter. Without a grid only the level moves.
 */
export function nextAreaFit(fit: AreaFit, measure: FoldMeasure): AreaFit {
  if (measure.kind === 'fold') {
    // A level chosen while the page was still loading (skeletons, a strip not yet drawn) is loosened again once the
    // first screen has clearly more room than one level saves, so loosening cannot overflow and bounce back.
    if (fit.level > 0 && -measure.overflowPx > STEP_DOWN_ROOM_PX) {
      return { ...fit, level: (fit.level - 1) as FitLevel };
    }
    const level = nextFitLevel(fit.level, measure.overflowPx);
    return level === fit.level ? fit : { ...fit, level };
  }
  const rows = Math.max(1, measure.rows);
  const wanted = fit.previewHeight + measure.roomPx / rows;
  const tooShort = wanted < PREVIEW_MIN_PX - OVERFLOW_TOLERANCE_PX;
  if (tooShort && fit.level < MAX_FIT_LEVEL) {
    return { foldGap: 0, level: (fit.level + 1) as FitLevel, overflows: false, previewHeight: PREVIEW_MIN_PX };
  }
  const tallest = Math.max(PREVIEW_MIN_PX, Math.floor(measure.cardWidthPx * PREVIEW_MAX_RATIO));
  const previewHeight = Math.floor(Math.min(Math.max(wanted, PREVIEW_MIN_PX), tallest));
  const foldGap = Math.max(0, Math.floor(measure.roomPx - (previewHeight - fit.previewHeight) * rows));
  const same =
    Math.abs(previewHeight - fit.previewHeight) <= OVERFLOW_TOLERANCE_PX &&
    Math.abs(foldGap - fit.foldGap) <= OVERFLOW_TOLERANCE_PX &&
    tooShort === fit.overflows;
  return same ? fit : { foldGap, level: fit.level, overflows: tooShort, previewHeight };
}

/** Spare room under the certified row, in px, that still counts as "the row reaches the bottom edge" (rounding). */
const FOLD_GAP_TOLERANCE_PX = 8;

/**
 * The cue closes the first screen only when the certified row reaches the screen's bottom edge. On a screen tall
 * enough that the previews stop at their tallest, the sections below simply follow the row, with no gap and no cue.
 */
export function foldCueShown(fit: Pick<AreaFit, 'foldGap' | 'overflows'>): boolean {
  return !fit.overflows && fit.foldGap < FOLD_GAP_TOLERANCE_PX;
}

export function scrollParentOf(element: HTMLElement): HTMLElement {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return node;
    }
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** An element's offset from the top of the scroller's content, whatever the scroll position. */
function contentOffset(scroller: HTMLElement, rect: DOMRect, edge: 'top' | 'bottom'): number {
  const isDocument = scroller === document.scrollingElement || scroller === document.documentElement;
  const scrollerTop = isDocument ? 0 : scroller.getBoundingClientRect().top;
  return rect[edge] - scrollerTop + scroller.scrollTop;
}

function measureFold(content: HTMLElement, scroller: HTMLElement): FoldMeasure | null {
  const grid = content.querySelector<HTMLElement>(`[${FOLD_GRID_ATTR}]`);
  if (grid) {
    const cards = Array.from(grid.children) as Array<HTMLElement>;
    const rows = new Set(cards.map((card) => card.offsetTop)).size;
    // The gap above the cue sits under the grid, so the room read here never includes the fit's own output.
    const gridBottom = contentOffset(scroller, grid.getBoundingClientRect(), 'bottom');
    return {
      cardWidthPx: cards[0]?.offsetWidth ?? grid.offsetWidth,
      kind: 'grid',
      roomPx: scroller.clientHeight - FOLD_CUE_HEIGHT_PX - gridBottom,
      rows,
    };
  }
  const fold = content.querySelector<HTMLElement>(`[${FOLD_ATTR}]`);
  if (!fold) {
    return null;
  }
  return {
    kind: 'fold',
    overflowPx: contentOffset(scroller, fold.getBoundingClientRect(), 'top') - scroller.clientHeight,
  };
}

/**
 * Fits a page's first screen: the title, the metric strip and the certified row end on the screen's bottom
 * edge, with the sections below it a scroll away. The certified previews stretch or shrink to do it; when
 * they reach their shortest, the page packs one level tighter.
 *
 * It starts again from the full design when the screen changes size. While `enabled` is off (Explore is
 * open) it holds the last fit and measures nothing, so opening and closing Explore moves nothing above it.
 */
export function useFitLevel(contentRef: RefObject<HTMLElement | null>, enabled: boolean): AreaFit {
  const [fit, setFit] = useState<AreaFit>(UNFITTED);
  const fitRef = useRef(fit);
  fitRef.current = fit;
  const sizeRef = useRef<string | null>(null);

  /** True when the fit changed, so a re-render (and its measuring pass) follows. */
  const apply = (next: AreaFit): boolean => {
    if (next === fitRef.current) {
      return false;
    }
    fitRef.current = next;
    setFit(next);
    return true;
  };

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!enabled || !content) {
      return undefined;
    }
    const scroller = scrollParentOf(content);
    const sizeOf = () => `${scroller.clientWidth}x${scroller.clientHeight}`;
    // A screen that changed size while Explore was open is fitted again from the full design.
    if (sizeRef.current !== null && sizeRef.current !== sizeOf()) {
      apply(UNFITTED);
    }
    sizeRef.current = sizeOf();
    // Without ResizeObserver (old browsers, the test DOM) the per-render pass below still fits the first view.
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      if (sizeOf() !== sizeRef.current) {
        sizeRef.current = sizeOf();
        // Already at the full design, nothing re-renders, so it is measured here instead.
        if (apply(UNFITTED)) {
          return;
        }
      }
      const measure = measureFold(content, scroller);
      if (measure) {
        apply(nextAreaFit(fitRef.current, measure));
      }
    });
    observer.observe(scroller);
    observer.observe(content);
    return () => observer.disconnect();
    // `apply` only touches refs and state setters.
  }, [contentRef, enabled]);

  // Runs after every render so content that arrives with the page's own state is measured before paint.
  useLayoutEffect(() => {
    const content = contentRef.current;
    // A reset from the effect above is waiting for its render; measuring from this render's fit would undo it.
    if (!enabled || !content || fitRef.current !== fit) {
      return;
    }
    const measure = measureFold(content, scrollParentOf(content));
    if (measure) {
      apply(nextAreaFit(fit, measure));
    }
  });

  return fit;
}
